import dayjs, { BusinessHoursMap, Dayjs } from "dayjs";
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import dayjsBusinessTime from "dayjs-business-time";
import DateHolidays from "date-holidays";
import customParseFormat from "dayjs/plugin/customParseFormat";
import minMax from "dayjs/plugin/minMax";
import { getTimezoneForCountry } from "./logic/timezone";
import { convertNumberToSpanishWords } from "./words";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(dayjsBusinessTime);
dayjs.extend(customParseFormat);
dayjs.extend(minMax);

// Default setup for Colombia
const hdColombia = new DateHolidays("CO");
const currentYear = dayjs().tz("America/Bogota").year();
const colombiaHolidays = [
  ...hdColombia.getHolidays(currentYear),
  ...hdColombia.getHolidays(currentYear + 1),
].map((holiday) => holiday.date.slice(0, 10));
dayjs.setHolidays(colombiaHolidays);

// Setup for Dominican Republic
const hdDominicanRepublic = new DateHolidays("DO");
const dominicanRepublicHolidays = [
  ...hdDominicanRepublic.getHolidays(currentYear),
  ...hdDominicanRepublic.getHolidays(currentYear + 1),
].map((holiday) => holiday.date.slice(0, 10));

// Cache for holidays by country
const holidaysByCountry: Record<string, string[]> = {
  CO: colombiaHolidays,
  DO: dominicanRepublicHolidays,
};

/**
 * Gets holidays for a specific country, with caching
 */
export const getHolidaysForCountry = (countryCode: string): string[] => {
  if (holidaysByCountry[countryCode]) {
    return holidaysByCountry[countryCode];
  }

  try {
    const hd = new DateHolidays(countryCode);
    const holidays = [...hd.getHolidays(currentYear), ...hd.getHolidays(currentYear + 1)].map(
      (holiday) => holiday.date.slice(0, 10)
    );
    holidaysByCountry[countryCode] = holidays;
    return holidays;
  } catch {
    // Fallback to Colombia holidays if country is not supported
    return colombiaHolidays;
  }
};

export const now = (timezone?: string): Dayjs => {
  return dayjs().tz(timezone || "America/Bogota");
};

export const nowStr = (timezone?: string): string => {
  return now(timezone).format("YYYY-MM-DD HH:mm:ss");
};

export const date = (date: string | number, customFormat?: string, timezone?: string): Dayjs => {
  const tz = timezone || "America/Bogota";
  try {
    return typeof date === "number" || /(?:Z|[+-]\d{2}:?\d{2})$/.test(date)
      ? dayjs(date, customFormat).tz(tz) // timezone info in datetime string
      : customFormat // no timezone info in datetime string -> uses specified timezone
      ? dayjs.tz(date, customFormat, tz)
      : dayjs.tz(date, tz);
  } catch {
    return dayjs(date, customFormat).tz(tz);
  }
};

// Converts an inclusive calendar-day range like ("2026-04-01", "2026-04-30")
// into a half-open UTC range [start, end) where end is exclusive (end + 1 day).
// Returns undefined if either bound is missing — meaning "no filter".
// Both bounds are interpreted in the configured timezone (defaults to America/Bogota).
export const toUtcDateRange = (
  start?: string,
  end?: string,
  timezone?: string
): { start: string; end: string } | undefined => {
  if (!start || !end) return undefined;
  return {
    start: date(start, undefined, timezone).utc().toISOString(),
    end: date(end, undefined, timezone).add(1, "day").utc().toISOString(),
  };
};

/**
 * Parses a date string with strict format validation
 * @param dateStr - The date string to parse
 * @param format - The expected format (e.g., "DD-MM-YYYY")
 * @param timezone - Optional timezone (defaults to America/Bogota)
 * @returns Dayjs object if valid, undefined if invalid
 */
export const parseStrictDate = (
  dateStr: string,
  format: string,
  timezone?: string
): Dayjs | undefined => {
  if (!dateStr?.trim()) {
    return undefined;
  }

  const trimmed = dateStr.trim();
  const tz = timezone || "America/Bogota";

  // Use strict parsing (third parameter true) to ensure exact format match
  const parsed = dayjs(trimmed, format, true);

  if (!parsed.isValid()) {
    return undefined;
  }

  // Apply timezone if needed
  return parsed.tz(tz);
};

export type TimeOfDay = { hour: number; minute: number };

/**
 * Parses a wall-clock time of day, accepting both shapes the config has historically
 * stored: the "8:00 AM" the portal writes and the "08:00:00" that business-hours configs
 * are normalized to. Returns null when the value is neither.
 *
 * Shared deliberately: three places used to parse these same strings their own way, and
 * one of them (`formatTime` in `getBusinessTimeConfig`) silently produced "Invalid Date"
 * for the 24-hour shape that a sibling function in the same file went out of its way to
 * handle.
 */
export const parseTimeOfDay = (value: string): TimeOfDay | null => {
  const trimmed = value.trim();

  const meridiem = /^(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]\.?$/.exec(trimmed);
  if (meridiem) {
    const base = Number(meridiem[1]) % 12;
    return {
      hour: meridiem[3].toLowerCase() === "p" ? base + 12 : base,
      minute: Number(meridiem[2]),
    };
  }

  const twentyFour = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (twentyFour) {
    const hour = Number(twentyFour[1]);
    const minute = Number(twentyFour[2]);
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
  }

  return null;
};

/** "HH:mm:ss" — the shape the dayjs business-time plugin expects. */
export const formatTimeOfDay = (time: TimeOfDay): string =>
  `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}:00`;

export const isBetween = (x: Dayjs, start: Dayjs | string | number, end: Dayjs | string | number) =>
  !x.isBefore(start) && !x.isAfter(end);

export const formatSpanish = (date: Dayjs, format: string): string =>
  date.locale("es").format(format);

export const isHoliday = (date: Dayjs | string | number): boolean => {
  const formattedDate = date instanceof dayjs ? date : dayjs(date);
  return formattedDate.isHoliday();
};

const defaultBusinessHours: BusinessHoursMap = {
  sunday: null,
  monday: [{ start: "09:00:00", end: "17:00:00" }],
  tuesday: [{ start: "09:00:00", end: "17:00:00" }],
  wednesday: [{ start: "09:00:00", end: "17:00:00" }],
  thursday: [{ start: "09:00:00", end: "17:00:00" }],
  friday: [{ start: "09:00:00", end: "17:00:00" }],
  saturday: null,
};

export const runWithBusinessHoursAsync = async <T>({
  fn,
  config,
}: {
  fn: () => Promise<T>;
  config: {
    businessHours: BusinessHoursMap;
    holidays?: string[];
    countryCode?: string;
  };
}): Promise<T> => {
  dayjs.setBusinessTime(config.businessHours);

  // Use country-specific holidays if provided, otherwise use config holidays or Colombia defaults
  const baseHolidays = config.countryCode
    ? getHolidaysForCountry(config.countryCode)
    : colombiaHolidays;
  const allHolidays = config.holidays ? [...baseHolidays, ...config.holidays] : baseHolidays;
  dayjs.setHolidays(allHolidays);

  try {
    return await fn();
  } finally {
    // Reset to defaults even if fn() throws, so the mutated global state
    // doesn't leak into the next call in a warm Lambda container.
    dayjs.setBusinessTime(defaultBusinessHours);
    dayjs.setHolidays(colombiaHolidays);
  }
};

export const runWithBusinessHoursSync = <T>({
  fn,
  config,
}: {
  fn: () => T;
  config: {
    businessHours: BusinessHoursMap;
    holidays?: string[];
    countryCode?: string;
  };
}): T => {
  dayjs.setBusinessTime(config.businessHours);

  // Use country-specific holidays if provided, otherwise use config holidays or Colombia defaults
  const baseHolidays = config.countryCode
    ? getHolidaysForCountry(config.countryCode)
    : colombiaHolidays;
  const allHolidays = config.holidays ? [...baseHolidays, ...config.holidays] : baseHolidays;
  dayjs.setHolidays(allHolidays);

  try {
    return fn();
  } finally {
    // Reset to defaults even if fn() throws, so the mutated global state
    // doesn't leak into the next call in a warm Lambda container.
    dayjs.setBusinessTime(defaultBusinessHours);
    dayjs.setHolidays(colombiaHolidays);
  }
};

export const formatDateWithTimezone = (dateString: string, countryCode: string): string => {
  const timezone = getTimezoneForCountry(countryCode);

  try {
    const parsedDate = date(dateString, undefined, timezone);
    return formatSpanish(parsedDate, "DD/MM/YYYY, HH:mm:ss");
  } catch (error) {
    // Fallback to default formatting if timezone conversion fails
    return dayjs(dateString).format("DD/MM/YYYY, HH:mm:ss");
  }
};

export const formatDateWithWords = (dateObj: Dayjs): string => {
  const dayOfWeek = dateObj.format("dddd");
  const day = dateObj.date();
  const month = dateObj.format("MMMM");
  const year = dateObj.year();
  const hour = dateObj.hour();
  const minute = dateObj.minute();

  const dayInWords = convertNumberToSpanishWords(day);

  // Convert hour to 12-hour format
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const hourInWords = convertNumberToSpanishWords(hour12);

  // Handle gender correction for "Uno" -> "Una" in Spanish time
  const correctedHourInWords = hour12 === 1 ? "Una" : hourInWords;

  // Handle minutes
  let timeString = correctedHourInWords;
  if (minute > 0) {
    const minuteInWords = convertNumberToSpanishWords(minute);
    timeString += ` y ${minuteInWords}`;
  }

  // Determine period of day based on specific ranges
  let periodOfDay: string;
  if (hour >= 0 && hour <= 11) {
    // 00:00 AM - 11:59 AM = "de la mañana"
    periodOfDay = "de la mañana";
  } else if (hour === 12) {
    // 12:00 PM - 12:59 PM = "del día"
    periodOfDay = "del día";
  } else if (hour >= 13 && hour <= 17) {
    // 1:00 PM - 5:59 PM = "de la tarde"
    periodOfDay = "de la tarde";
  } else {
    // 6:00 PM - 11:59 PM = "de la noche"
    periodOfDay = "de la noche";
  }

  // Handle singular "la" vs plural "las" for time
  const timeArticle = hour12 === 1 ? "la" : "las";

  return `${dayOfWeek} ${dayInWords} de ${month} a ${timeArticle} ${timeString} ${periodOfDay}`;
};
